import React, { useState, useEffect } from 'react';
import { Menu, Search, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [submenuTimeout, setSubmenuTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: t('nav.home'), href: '/' },
    { 
      label: t('nav.mission'), 
      href: '#',
      submenu: [
        { label: t('nav.about'), href: '/about' },
        { label: t('nav.experts'), href: '/experts' }
      ]
    },
    { label: t('nav.program'), href: '/program' },
    { label: t('nav.faq'), href: '/faq' },
    { label: t('nav.blog'), href: '/blog' },
    { label: t('nav.contact'), href: '/contact' }
  ];

  const handleSubmenuEnter = (label: string) => {
    if (submenuTimeout) {
      clearTimeout(submenuTimeout);
      setSubmenuTimeout(null);
    }
    setActiveSubmenu(label);
  };

  const handleSubmenuLeave = () => {
    const timeout = setTimeout(() => {
      setActiveSubmenu(null);
    }, 300);
    setSubmenuTimeout(timeout);
  };

  const navVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const menuVariants = {
    closed: { opacity: 0, x: '100%' },
    open: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const searchVariants = {
    closed: { opacity: 0, y: -20 },
    open: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const submenuVariants = {
    closed: { 
      opacity: 0,
      y: -10,
      pointerEvents: 'none'
    },
    open: { 
      opacity: 1,
      y: 0,
      pointerEvents: 'auto',
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const renderNavLink = (item: any) => {
    if (item.href.startsWith('/')) {
      return (
        <Link 
          to={item.href}
          className="relative px-4 py-2 text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300 font-serif text-sm tracking-widest group inline-flex items-center"
        >
          {item.label}
          {item.submenu && <ChevronDown className="ml-1 w-4 h-4" />}
          <span className="absolute left-0 right-0 bottom-0 h-px bg-[#c9ab81] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center" />
        </Link>
      );
    }
    return (
      <a 
        href={item.href}
        className="relative px-4 py-2 text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300 font-serif text-sm tracking-widest group inline-flex items-center"
      >
        {item.label}
        {item.submenu && <ChevronDown className="ml-1 w-4 h-4" />}
        <span className="absolute left-0 right-0 bottom-0 h-px bg-[#c9ab81] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center" />
      </a>
    );
  };

  return (
    <>
      <motion.nav
        initial="hidden"
        animate="visible"
        variants={navVariants}
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled ? 'bg-[#f5f5f5]/90 backdrop-blur-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24 border-b border-[#41392e]/10">
            <Link to="/" className="block">
              <Logo />
            </Link>

            <div className="hidden lg:flex items-center justify-center flex-1 px-8">
              {menuItems.map((item, index) => (
                <div 
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => handleSubmenuEnter(item.label)}
                  onMouseLeave={handleSubmenuLeave}
                >
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.5,
                      delay: index * 0.1,
                      ease: [0.16, 1, 0.3, 1]
                    }}
                  >
                    {renderNavLink(item)}
                  </motion.div>

                  {item.submenu && (
                    <motion.div
                      className="absolute top-full left-0 mt-2 py-2 min-w-[200px] bg-[#f5f5f5]/90 backdrop-blur-sm border border-[#41392e]/10"
                      initial="closed"
                      animate={activeSubmenu === item.label ? "open" : "closed"}
                      variants={submenuVariants}
                      onMouseEnter={() => handleSubmenuEnter(item.label)}
                      onMouseLeave={handleSubmenuLeave}
                    >
                      <div className="absolute -top-4 left-0 right-0 h-4 bg-transparent" />
                      {item.submenu.map((subItem: any) => (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          className="block px-4 py-2 text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300 font-serif text-sm tracking-widest"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-6">
              <LanguageSwitcher />
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300"
              >
                <Search className="w-5 h-5" />
              </button>
              <button
                className="lg:hidden text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={searchVariants}
              className="absolute top-full left-0 w-full bg-[#f5f5f5]/95 border-b border-[#41392e]/10"
            >
              <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    className="w-full bg-transparent border-b border-[#41392e]/10 py-4 text-xl font-serif text-[#41392e] placeholder-[#41392e]/50 focus:outline-none focus:border-[#c9ab81]"
                    autoFocus
                  />
                  <button
                    onClick={() => setIsSearchOpen(false)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="fixed inset-y-0 right-0 w-full lg:hidden bg-[#f5f5f5] z-40 border-l border-[#41392e]/10"
          >
            <div className="h-full flex flex-col pt-24 px-6">
              {menuItems.map((item, index) => (
                <div key={item.label}>
                  {item.href.startsWith('/') ? (
                    <Link
                      to={item.href}
                      className="py-4 text-lg font-serif text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300 border-b border-[#41392e]/10 flex items-center justify-between"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                      {item.submenu && (
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${
                          activeSubmenu === item.label ? 'rotate-180' : ''
                        }`} />
                      )}
                    </Link>
                  ) : (
                    <motion.a
                      href={item.href}
                      className="py-4 text-lg font-serif text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300 border-b border-[#41392e]/10 flex items-center justify-between"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.4,
                        delay: index * 0.1,
                        ease: [0.16, 1, 0.3, 1]
                      }}
                      onClick={() => {
                        if (item.submenu) {
                          setActiveSubmenu(activeSubmenu === item.label ? null : item.label);
                        } else {
                          setIsMenuOpen(false);
                        }
                      }}
                    >
                      {item.label}
                      {item.submenu && (
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${
                          activeSubmenu === item.label ? 'rotate-180' : ''
                        }`} />
                      )}
                    </motion.a>
                  )}
                  {item.submenu && (
                    <motion.div
                      initial="closed"
                      animate={activeSubmenu === item.label ? "open" : "closed"}
                      variants={submenuVariants}
                      className="pl-4"
                    >
                      {item.submenu.map((subItem: any) => (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          className="block py-3 text-base font-serif text-[#41392e]/80 hover:text-[#c9ab81] transition-colors duration-300"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;